import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  acceptGroupInvite,
  createGroup,
  deleteGroup,
  getGroup,
  getGroupLeaderboard,
  inviteToGroup,
  joinGroup,
  removeGroupMember,
  ApiError,
} from "../api";
import {
  AcceptGroupInviteResponse,
  CreateGroupBody,
  CreateGroupResponse,
  GetGroupResponse,
  GroupLeaderboardResponse,
  InviteToGroupBody,
  JoinGroupResponse,
  MessageResponse,
} from "../api/types";

const ONE_HOUR_MS = 60 * 60 * 1000;

export const groupKeys = {
  all: ["group"] as const,
  detail: (groupId?: string) => [...groupKeys.all, groupId] as const,
  leaderboard: (
    groupId: string,
    date?: string,
    limit?: number,
    offset?: number,
  ) => [...groupKeys.all, groupId, "leaderboard", date, limit, offset] as const,
};

export function useGroup(groupId: string | undefined) {
  return useQuery<GetGroupResponse, ApiError>({
    queryKey: groupKeys.detail(groupId),
    queryFn: () => getGroup(groupId as string),
    enabled: !!groupId,
  });
}

export function useGroups(groupIds: string[]) {
  return useQueries({
    queries: groupIds.map((groupId) => ({
      queryKey: groupKeys.detail(groupId),
      queryFn: () => getGroup(groupId),
    })),
  });
}

export function useCreateGroup() {
  return useMutation<CreateGroupResponse, ApiError, CreateGroupBody>({
    mutationFn: createGroup,
  });
}

export function useDeleteGroup() {
  const queryClient = useQueryClient();

  return useMutation<MessageResponse, ApiError, string>({
    mutationFn: deleteGroup,
    onSuccess: (_, groupId) => {
      queryClient.removeQueries({ queryKey: groupKeys.detail(groupId) });
    },
  });
}

export function useJoinGroup() {
  const queryClient = useQueryClient();

  return useMutation<
    JoinGroupResponse,
    ApiError,
    { groupId: string; joinCode?: string }
  >({
    mutationFn: ({ groupId, joinCode }) => joinGroup(groupId, { joinCode }),
    onSuccess: (_, { groupId }) => {
      queryClient.invalidateQueries({ queryKey: groupKeys.detail(groupId) });
    },
  });
}

export function useInviteToGroup() {
  return useMutation<
    MessageResponse,
    ApiError,
    { groupId: string } & InviteToGroupBody
  >({
    mutationFn: ({ groupId, ...body }) => inviteToGroup(groupId, body),
  });
}

export function useAcceptGroupInvite() {
  const queryClient = useQueryClient();

  return useMutation<AcceptGroupInviteResponse, ApiError, string>({
    mutationFn: acceptGroupInvite,
    onSuccess: ({ groupId }) => {
      queryClient.invalidateQueries({ queryKey: groupKeys.detail(groupId) });
    },
  });
}

export function useGroupLeaderboard(
  groupId: string | undefined,
  params?: { date?: string; limit?: number; offset?: number },
) {
  return useQuery<GroupLeaderboardResponse, ApiError>({
    queryKey: groupKeys.leaderboard(
      groupId as string,
      params?.date,
      params?.limit,
      params?.offset,
    ),
    queryFn: () => getGroupLeaderboard(groupId as string, params),
    enabled: !!groupId,
    staleTime: ONE_HOUR_MS,
  });
}

export function useRemoveGroupMember() {
  const queryClient = useQueryClient();

  return useMutation<
    MessageResponse,
    ApiError,
    { groupId: string; playerId: string }
  >({
    mutationFn: ({ groupId, playerId }) => removeGroupMember(groupId, playerId),
    onSuccess: (_, { groupId }) => {
      queryClient.invalidateQueries({ queryKey: groupKeys.detail(groupId) });
    },
  });
}
